import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { gsap } from "gsap";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { trackEvent, trackPageView } from "../utils/tracking";
import { getVideoUrl, getImageUrl, getCSSImageUrl } from "../utils/mediaUrl";
import { media, rgba } from "../styles/stylesTheme";
import { borderRadius } from "../styles/stylesUtils";

gsap.registerPlugin(MotionPathPlugin);

// Animations
const float = keyframes`
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
`;

const grain = keyframes`
    0%, 100% { transform: translate(0, 0); }
    10% { transform: translate(-5%, -10%); }
    20% { transform: translate(-15%, 5%); }
    30% { transform: translate(7%, -25%); }
    40% { transform: translate(-5%, 25%); }
    50% { transform: translate(-15%, 10%); }
    60% { transform: translate(15%, 0%); }
    70% { transform: translate(0%, 15%); }
    80% { transform: translate(3%, 35%); }
    90% { transform: translate(-10%, 10%); }
`;

// Styled Components
const Page = styled.div`
    min-height: 100vh;
    background: linear-gradient(
        111deg,
        ${({ theme }) => rgba(theme, "gradL", 1)} 0%,
        ${({ theme }) => rgba(theme, "gradR", 1)} 100%
    );
    color: ${({ theme }) => rgba(theme, "black", 1)};
    overflow-x: hidden;
    position: relative;
    box-sizing: border-box;

    *,
    *::before,
    *::after {
        box-sizing: border-box;
    }

    &::before {
        content: "";
        position: fixed;
        top: -50%;
        left: -50%;
        right: -50%;
        bottom: -50%;
        width: 200%;
        height: 200%;
        background: transparent
            url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")
            repeat;
        opacity: 0.08;
        pointer-events: none;
        animation: ${grain} 8s steps(10) infinite;
        z-index: 1000;
    }

    ${media.mobile} {
        padding: 1em;
        font-size: 120%;
    }
`;

const Section = styled.section`
    position: relative;
    z-index: 1;
`;

const Logo = styled.a`
    position: fixed;
    top: 2.9em;
    left: 2.9em;
    width: 4.8em;
    height: 4em;
    z-index: 1000;
    background: ${({ theme }) => rgba(theme, "colorAccent", 1)};
    mask-image: url("${getCSSImageUrl("img/logo.svg")}");
    -webkit-mask-image: url("${getCSSImageUrl("img/logo.svg")}");
    mask-size: contain;
    -webkit-mask-size: contain;
    mask-repeat: no-repeat;
    -webkit-mask-repeat: no-repeat;
    mask-position: center;
    -webkit-mask-position: center;
    cursor: pointer;
`;

const FixedCTA = styled(motion.button)`
    position: fixed;
    top: 2.6em;
    right: 2.6em;
    z-index: 1000;
    font-family: var(--bold);
    font-size: 1em;
    padding: 0;
    background: linear-gradient(
        180deg,
        ${({ theme }) => rgba(theme, "colorCorrect", 1)} 0%,
        ${({ theme }) => rgba(theme, "colorCorrect", 0.85)} 100%
    );
    color: white;
    text-shadow: 0 1px 0 ${({ theme }) => rgba(theme, "colorCorrect", 0.6)};
    border: none;
    ${borderRadius(0.5)}
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: filter 0.15s ease;
    user-select: none;
    -webkit-user-select: none;
    box-shadow: 0 0.1em 0 0 #eb450e;
    box-shadow: 0 0.1em 0 0 color(display-p3 0.8518 0.3236 0.1629);

    &:hover {
        filter: brightness(1.1);
    }

    &:active {
        transform: translateY(0.08em) scale(0.997);
        box-shadow: 0 0 0 0 #eb450e;
        box-shadow: 0 0 0 0 color(display-p3 0.8518 0.3236 0.1629);
    }
`;

const FixedCTAInner = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5em;
    padding: 1.2em 1.4em 1.25em;
    ${borderRadius(0.48)}
    box-shadow: inset 0 0.1em 0.1em 0 ${({ theme }) =>
        rgba(theme, "white", 0.3)},
        inset 0 0 0.1em 0 ${({ theme }) => rgba(theme, "black", 0.5)};
`;

// Hero Section
const Hero = styled(Section)`
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4em;
    position: relative;

    ${media.mobile} {
        padding: 2em;
        padding-top: 10em;
        align-items: flex-start;
    }
`;

const HeroInner = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4em;
    align-items: center;
    max-width: 1400px;
    width: 100%;

    ${media.tablet} {
        grid-template-columns: 1fr;
        text-align: center;
        gap: 3em;
    }
`;

const HeroContent = styled.div`
    position: relative;
    z-index: 3;

    ${media.tablet} {
        order: 1;
    }
`;

const HeroVisual = styled.div`
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;

    ${media.tablet} {
        order: 2;
    }
`;

const Eyebrow = styled(motion.div)`
    font-family: "Font Med", -apple-system, sans-serif;
    font-size: 0.75em;
    //letter-spacing: 0.3em;
    //text-transform: uppercase;
    color: ${({ theme }) => rgba(theme, "colorAccent", 1)};
    margin-bottom: 1.5em;
    background: ${({ theme }) => rgba(theme, "colorAccent", 0.1)};
    display: inline-block;
    padding: 0.5em 0.9em 0.6em;
    ${borderRadius(0.5)}
`;

const HeroTitle = styled(motion.h1)`
    font-family: "Gonz", Georgia, serif;
    font-size: clamp(2.5em, 5vw, 3em);
    font-weight: 400;
    line-height: 1.25;
    margin: 0 0 0.5em;
    letter-spacing: -0.02em;
    max-width: 14em;

    span {
        //display: block;
    }

    .highlight {
        color: ${({ theme }) => rgba(theme, "colorAccent", 1)};
    }

    ${media.tablet} {
        margin: 0 auto 0.5em;
    }
`;

const HeroSubtitle = styled(motion.p)`
    font-family: "Font", -apple-system, sans-serif;
    font-size: clamp(1em, 2vw, 1.25em);
    font-weight: 300;
    line-height: 1.5;
    color: ${({ theme }) => rgba(theme, "black", 0.7)};
    max-width: 30em;
    margin: 0 0 2.5em;

    ${media.tablet} {
        margin: 0 auto 2.5em;
    }
`;

const ProductLink = styled.a`
    color: ${({ theme }) => rgba(theme, "colorAccent", 1)};
    font-family: "Font Med", -apple-system, sans-serif;
    text-decoration: underline;
    text-underline-offset: 0.15em;
`;

const CTAButton = styled(motion.button)`
    font-family: var(--bold);
    font-size: 1.4em;
    padding: 0;
    background: linear-gradient(
        180deg,
        ${({ theme }) => rgba(theme, "colorCorrect", 1)} 0%,
        ${({ theme }) => rgba(theme, "colorCorrect", 0.85)} 100%
    );
    color: white;
    text-shadow: 0 1px 0 ${({ theme }) => rgba(theme, "colorCorrect", 0.6)};
    border: none;
    ${borderRadius(0.5)}
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: 0.15s ease;
    position: relative;
    user-select: none;
    -webkit-user-select: none;
    box-shadow: 0 0.1em 0 0 #eb450e;
    box-shadow: 0 0.1em 0 0 color(display-p3 0.8518 0.3236 0.1629);

    &:hover {
        filter: brightness(1.1);
    }

    &:active {
        transform: translateY(0.08em) scale(0.997);
        box-shadow: 0 0 0 0 #eb450e;
        box-shadow: 0 0 0 0 color(display-p3 0.8518 0.3236 0.1629);
    }

    svg {
        width: 1.25em;
        height: 1.25em;
    }
`;

const CTAButtonInner = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.6em;
    padding: 0.9em 1.2em;
    ${borderRadius(0.48)}
    box-shadow: inset 0 0.1em 0.1em 0 ${({ theme }) =>
        rgba(theme, "white", 0.3)},
        inset 0 0 0.1em 0 ${({ theme }) => rgba(theme, "black", 0.5)};
`;

// How It Works Section
const HowItWorks = styled(Section)`
    padding: 8em 2em;

    ${media.mobile} {
        padding: 4em 1.5em;
    }
`;

const SectionHeader = styled.div`
    text-align: center;
    margin-bottom: 5em;

    ${media.mobile} {
        margin-bottom: 3em;
    }
`;

const SectionTitle = styled(motion.h2)`
    font-family: "Gonz", Georgia, serif;
    font-size: clamp(2em, 5vw, 3em);
    font-weight: 400;
    margin: 0 0 0.2em;
`;

const SectionSubtitle = styled(motion.p)`
    font-family: "Font", -apple-system, sans-serif;
    font-size: 1em;
    color: ${({ theme }) => rgba(theme, "black", 0.6)};
    max-width: 500px;
    margin: 0 auto;
`;

const StepsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2em;
    max-width: 1100px;
    margin: 0 auto;

    ${media.mobile} {
        grid-template-columns: 1fr;
        gap: 3em;
    }
`;

const Step = styled(motion.div)`
    text-align: center;
    position: relative;

    &:not(:last-child)::after {
        content: "";
        position: absolute;
        top: 3em;
        right: -1em;
        width: 2em;
        height: 1px;
        background: linear-gradient(
            -90deg,
            ${({ theme }) => rgba(theme, "colorAccent", 0.5)},
            transparent
        );

        ${media.mobile} {
            display: none;
        }
    }
`;

const StepNumber = styled.div`
    width: 4em;
    height: 4em;
    border-radius: 50%;
    background: ${({ theme }) => rgba(theme, "colorAccent", 0.1)};
    border: 1px solid ${({ theme }) => rgba(theme, "colorAccent", 0.3)};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5em;
    font-family: "Gonz", Georgia, serif;
    font-size: 1.5em;
    color: ${({ theme }) => rgba(theme, "colorAccent", 1)};
`;

const StepTitle = styled.h3`
    font-family: "Font Med", -apple-system, sans-serif;
    font-size: 1.1em;
    margin: 0 0 0.3em;
`;

const StepDesc = styled.p`
    font-family: "Font", -apple-system, sans-serif;
    font-size: 0.9em;
    color: ${({ theme }) => rgba(theme, "black", 0.6)};
    line-height: 1.6;
    margin: 0;
`;

// Features Section
const Features = styled(Section)`
    padding: 8em 2em;

    ${media.mobile} {
        padding: 4em 1.5em;
    }
`;

const FeatureGrid = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 10em;

    ${media.mobile} {
        gap: 5em;
    }
`;

const FeatureRow = styled(motion.div)`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4em;
    align-items: center;

    &:nth-child(even) {
        direction: rtl;
        > * {
            direction: ltr;
        }
    }

    ${media.mobile} {
        grid-template-columns: 1fr;
        gap: 2em;

        &:nth-child(even) {
            direction: ltr;
        }
    }
`;

const FeatureContent = styled.div``;

const FeatureLabel = styled.div`
    font-family: "Font Med", -apple-system, sans-serif;
    font-size: 0.7em;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: ${({ theme }) => rgba(theme, "colorAccent", 1)};
    margin-bottom: 1em;
`;

const FeatureTitle = styled.h3`
    font-family: "Gonz", Georgia, serif;
    font-size: clamp(1.75em, 4vw, 2.5em);
    font-weight: 400;
    margin: 0 0 0.3em;
    line-height: 1.2;
`;

const FeatureDesc = styled.p`
    font-family: "Font", -apple-system, sans-serif;
    font-size: 1em;
    color: ${({ theme }) => rgba(theme, "black", 0.7)};
    line-height: 1.7;
    margin: 0;
`;

const FeatureVisual = styled.div`
    aspect-ratio: 4/3;
    background: linear-gradient(
        135deg,
        ${({ theme }) => rgba(theme, "colorAI", 0.2)} 0%,
        ${({ theme }) => rgba(theme, "colorYou", 0.2)} 100%
    );
    border-radius: 1em;
    border: 1px solid ${({ theme }) => rgba(theme, "black", 0.08)};
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;

    &::before {
        content: "";
        position: absolute;
        inset: 0;
        background: radial-gradient(
            circle at 30% 30%,
            ${({ theme }) => rgba(theme, "colorAI", 0.15)} 0%,
            transparent 50%
        );
    }
`;

const PhoneMockup = styled.div`
    width: 8.75em;
    height: 17.81em;
    background: ${({ theme }) => rgba(theme, "colorAccent", 1)};
    ${borderRadius(1)};
    border: 3px solid ${({ theme }) => rgba(theme, "colorAccent", 0.8)};
    position: relative;
    box-shadow: 0 25px 50px ${({ theme }) => rgba(theme, "black", 0.2)},
        inset 0 0 0 1px rgba(255, 255, 255, 0.1);
    animation: ${float} 6s ease-in-out infinite;
    font-size: 1.1em;

    /* Dynamic Island */
    &::before {
        content: "";
        position: absolute;
        top: 0.56em;
        left: 50%;
        transform: translateX(-50%);
        width: 32%;
        height: 4%;
        background: ${({ theme }) => rgba(theme, "colorAccent", 0.6)};
        border-radius: 1.25em;
        z-index: 10;
    }

    /* Side button (right) */
    &::after {
        content: "";
        position: absolute;
        right: -0.23em;
        top: 3.93em;
        width: 0.17em;
        height: 1.97em;
        background: ${({ theme }) => rgba(theme, "colorAccent", 0.8)};
        border-radius: 0 0.13em 0.13em 0;
    }

    &.secondary {
        width: 6.67em;
        height: 13.72em;
        position: absolute;
        right: 15%;
        bottom: 10%;
        animation-delay: -3s;

        &::before {
            height: 0.45em;
            top: 0.45em;
        }

        &::after {
            top: 3.37em;
            height: 1.69em;
        }
    }
`;

const PhoneScreen = styled.div`
    position: absolute;
    top: 0.25em;
    left: 0.25em;
    right: 0.25em;
    bottom: 0.25em;
    background: ${({ theme }) => rgba(theme, "gradL", 1)};
    ${borderRadius(0.75)};
    display: flex;
    flex-direction: column;
    padding: 1.75em 0.625em 0.75em;
    gap: 0.5em;
`;

const VideoPlayer = styled.div`
    margin-top: auto;
    width: 100%;
    aspect-ratio: 16/9;
    background: #000;
    ${borderRadius(0.4)};
    position: relative;
    overflow: hidden;

    video {
        width: 100%;
        height: 100%;
        object-fit: cover;
        filter: blur(0.3em);
    }
`;

const BackgroundTV = styled.div`
    position: absolute;
    top: 0%;
    left: 0%;
    transform: translate(-50%, -50%);
    width: 50vw;
    aspect-ratio: 16/9;
    z-index: 0;
    opacity: 0.6;
    filter: blur(2em) saturate(0.3);
    -webkit-filter: blur(2em) saturate(0.3);
    overflow: hidden;
    ${borderRadius(1)};

    video {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const CaptionBubble = styled.div`
    background: ${({ theme }) => rgba(theme, "colorAI", 0.6)};
    ${borderRadius(0.45)};
    padding: 0.625em 0.8em;
    font-family: "Font", -apple-system, sans-serif;
    font-size: 0.5em;
    line-height: 1.4;
    color: ${({ theme }) => rgba(theme, "colortextAi", 1)};

    &.translated {
        background: ${({ theme }) => rgba(theme, "colorYou", 0.6)};
        color: ${({ theme }) => rgba(theme, "colortextYou", 1)};
        align-self: flex-end;
        max-width: 85%;
    }
`;

const HighlightWord = styled(motion.span)`
    background: ${({ theme }) => rgba(theme, "colorTranslate", 0.5)};
    padding: 0 0.125em;
    border-radius: 0.125em;
    position: relative;
    display: inline-block;
`;

const Cursor = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 3em;
    height: 4em;
    pointer-events: none;
    z-index: 10000;

    img {
        width: 100%;
        height: 100%;
    }
`;

const WordTooltip = styled(motion.div)`
    position: absolute;
    bottom: 100%;
    left: 50%;
    margin-bottom: 0.8em;
    border-radius: 1em;
    background: ${({ theme }) => rgba(theme, "colorTranslate", 0.3)};
    backdrop-filter: blur(1em);
    -webkit-backdrop-filter: blur(1em);
    box-shadow: 0px 1px 0.5em 0px rgba(43, 43, 46, 0.2);
    z-index: 50;
    font-size: 1em;
    padding: 0.2em;

    .tooltip-inner {
        border-radius: 0.8em;
        background: ${({ theme }) => rgba(theme, "colorTranslate", 1)};
        padding: 0.8em 3.2em 0.75em 1em;
        color: rgba(35, 34, 18, 0.9);
        text-align: center;
        line-height: 1.5em;
        white-space: nowrap;
        font-family: "Font Med", -apple-system, sans-serif;
        position: relative;
    }

    .tooltip-btn {
        width: 1.8em;
        height: 1.8em;
        position: absolute;
        top: 50%;
        right: 0.8em;
        transform: translateY(-50%);
        border: none;
        background: rgba(35, 34, 18, 0.1);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        font-size: 0.9em;
        color: rgba(35, 34, 18, 0.7);
        box-shadow: inset 0 0 0 0.15em rgba(35, 34, 18, 0.7);

        img {
            width: 0.9em;
            height: 0.9em;
            position: relative;
        }
    }

    /* Triangle pointer */
    &::after {
        content: "";
        position: absolute;
        bottom: -0.9em;
        left: 50%;
        transform: translateX(-50%);
        border-width: 0.6em;
        border-style: solid;
        border-color: ${({ theme }) => rgba(theme, "colorTranslate", 1)}
            transparent transparent transparent;
    }
`;

const GlossaryPreview = styled.div`
    position: absolute;
    top: 50%;
    right: 50%;
    transform: translateY(-50%);
    background: ${({ theme }) => rgba(theme, "white", 1)};
    border: 1px solid ${({ theme }) => rgba(theme, "black", 0.1)};
    border-radius: 0.75em;
    padding: 0.75em;
    width: 17.5em;
    font-family: "Font", -apple-system, sans-serif;
    font-size: 0.4375em;
    box-shadow: 0 0.625em 1.875em ${({ theme }) => rgba(theme, "black", 0.1)};
    font-size: 0.7em;

    .word {
        font-family: "Font Bold", -apple-system, sans-serif;
        font-size: 0.625em;
        color: ${({ theme }) => rgba(theme, "colorAccent", 1)};
        margin-bottom: 0.375em;
    }

    .definition {
        color: ${({ theme }) => rgba(theme, "black", 0.7)};
        line-height: 1.4;
    }
`;

// Glossary Modal
const GlossaryBackdrop = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 110000;
    padding: 1em;
    background: ${({ theme }) => rgba(theme, "gradL", 0.6)};
    backdrop-filter: blur(1em);
    -webkit-backdrop-filter: blur(1em);
`;

const GlossaryCloseButton = styled(motion.button)`
    position: absolute;
    top: 1em;
    right: 1em;
    width: 2.5em;
    height: 2.5em;
    border: none;
    background: ${({ theme }) => rgba(theme, "black", 0.05)};
    cursor: pointer;
    z-index: 10001;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s ease;

    &:hover {
        background: ${({ theme }) => rgba(theme, "black", 0.1)};
    }

    &:active {
        transform: scale(0.95);
    }

    &::before {
        content: "";
        width: 1.2em;
        height: 1.2em;
        background: ${({ theme }) => rgba(theme, "black", 1)};
        mask-image: url("${getCSSImageUrl("img/icon-x.svg")}");
        -webkit-mask-image: url("${getCSSImageUrl("img/icon-x.svg")}");
        mask-position: center;
        -webkit-mask-position: center;
        mask-repeat: no-repeat;
        -webkit-mask-repeat: no-repeat;
        mask-size: contain;
        -webkit-mask-size: contain;
        display: block;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translateY(-50%) translateX(-50%);
    }
`;

const GlossaryModal = styled(motion.div)`
    position: relative;
    background: ${({ theme }) => rgba(theme, "gradL", 1)};
    border-radius: 16px;
    padding: 2em;
    max-width: 40em;
    width: 100%;
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

    * {
        box-sizing: border-box;
    }

    ${media.mobile} {
        padding: 1.5em;
        max-height: calc(100svh - 5em);
        max-width: none;
        width: calc(100vw - 3em);
        font-size: 1.2em;
    }
`;

const GlossaryHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.3em;
    color: ${({ theme }) => rgba(theme, "colortextYou", 1)};
    position: relative;
`;

const GlossaryWordTitle = styled.h2`
    font-size: 2em;
    font-family: var(--bold);
    color: ${({ theme }) => rgba(theme, "colortextYou", 1)};
    margin: 0;
    line-height: 1.25em;
    margin-top: -0.1em;
`;

const GlossaryTranslation = styled.p`
    font-size: 1.2em;
    color: ${({ theme }) => rgba(theme, "colortextYou", 0.6)};
    margin: -0.3em 0 0;
    font-family: var(--med);
    line-height: 1.25em;
`;

const GlossaryWordType = styled.div`
    font-size: 0.85em;
    color: ${({ theme }) => rgba(theme, "colortextYou", 0.5)};
    font-style: italic;
    text-transform: capitalize;
    margin-top: -0.4em;
`;

const GlossarySection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75em;
`;

const GlossarySectionTitle = styled.h3`
    font-size: 1.1em;
    font-weight: 600;
    color: ${({ theme }) => rgba(theme, "colortextYou", 1)};
    margin: 2em 0 0;
    font-family: var(--bold);
`;

const GlossaryVariationsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1em;
    border-radius: 0.7em;
`;

const GlossaryVariationItem = styled.div`
    display: flex;
    flex-direction: column;
    color: ${({ theme }) => rgba(theme, "colortextYou", 0.6)};
    border-radius: 0.4em;
    background: ${({ theme }) => rgba(theme, "black", 0.07)};
    position: relative;
`;

const GlossaryVariationLabel = styled.div`
    font-size: 0.8em;
    color: ${({ theme }) => rgba(theme, "colortextYou", 0.6)};
    background: ${({ theme }) => rgba(theme, "black", 0.08)};
    padding: 0.7em 1em;
    font-family: var(--med);
    border-top-left-radius: 0.5em;
    border-top-right-radius: 0.5em;
    line-height: 1.35em;
`;

const GlossaryVariationValue = styled.div`
    font-size: 1em;
    color: ${({ theme }) => rgba(theme, "colortextYou", 1)};
    font-family: var(--med);
    padding: 0.7em 0.8em 0.3em;
    line-height: 1.4;
`;

const GlossaryVariationTranslation = styled.div`
    font-size: 0.85em;
    color: ${({ theme }) => rgba(theme, "colortextYou", 0.6)};
    font-style: italic;
    padding: 0em 0.95em 1em;
    line-height: 1.4;
`;

const GlossaryPhraseItem = styled.div`
    display: flex;
    flex-direction: column;
    color: ${({ theme }) => rgba(theme, "colortextYou", 0.6)};
    border-radius: 0.4em;
    background: ${({ theme }) => rgba(theme, "black", 0.1)};
    position: relative;
`;

const GlossaryPhrase = styled.div`
    font-size: 1em;
    color: ${({ theme }) => rgba(theme, "colortextYou", 1)};
    font-family: var(--med);
    padding: 0.7em 0.9em 0.2em;
    line-height: 1.4;
`;

const GlossaryPhraseTranslation = styled.div`
    font-size: 0.85em;
    color: ${({ theme }) => rgba(theme, "colortextYou", 0.6)};
    font-style: italic;
    padding: 0em 1em 0.9em;
    line-height: 1.4;
`;

// Final CTA Section
const FinalCTA = styled(Section)`
    padding: 10em 2em;
    text-align: center;
    position: relative;
    overflow: hidden;

    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: radial-gradient(
            ellipse at 50% 100%,
            ${({ theme }) => rgba(theme, "colorAccent", 0.1)} 0%,
            transparent 50%
        );
        pointer-events: none;
    }

    ${media.mobile} {
        padding: 5em 1.5em;
    }
`;

const FinalTitle = styled(motion.h2)`
    font-family: "Gonz", Georgia, serif;
    font-size: clamp(2em, 6vw, 4em);
    font-weight: 400;
    margin: 0 auto 1em;
    max-width: 700px;
    line-height: 1.2;

    .highlight {
        color: ${({ theme }) => rgba(theme, "colorAccent", 1)};
    }
`;

const TrustBadges = styled(motion.div)`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2em;
    margin-top: 3em;
    flex-wrap: wrap;
`;

const Badge = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5em;
    font-family: "Font", -apple-system, sans-serif;
    font-size: 0.85em;
    color: ${({ theme }) => rgba(theme, "black", 0.5)};

    svg {
        width: 1em;
        height: 1em;
        color: ${({ theme }) => rgba(theme, "colorAccent", 1)};
    }
`;

// Footer
const Footer = styled.footer`
    padding: 2em;
    text-align: center;
    border-top: 1px solid ${({ theme }) => rgba(theme, "black", 0.05)};

    p {
        font-family: "Font", -apple-system, sans-serif;
        font-size: 0.8em;
        color: ${({ theme }) => rgba(theme, "black", 0.3)};
        margin: 0;

        a {
            color: ${({ theme }) => rgba(theme, "colorAccent", 1)};
        }
    }
`;

// Animation variants
const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] },
    },
};

const stagger = {
    visible: { transition: { staggerChildren: 0.15 } },
};

// Component
const LandingPage = ({ onGoogleSignIn }) => {
    const heroRef = useRef(null);
    const isHeroInView = useInView(heroRef, { once: true });
    const isHeroVisible = useInView(heroRef, { amount: 0.3 });

    const howItWorksRef = useRef(null);
    const isHowItWorksInView = useInView(howItWorksRef, {
        once: true,
        margin: "-100px",
    });

    const featuresRef = useRef(null);
    const isFeaturesInView = useInView(featuresRef, {
        once: true,
        margin: "-100px",
    });

    const ctaRef = useRef(null);
    const isCtaInView = useInView(ctaRef, { once: true, margin: "-100px" });

    const cursorRef = useRef(null);
    const [showGlossary, setShowGlossary] = useState(false);

    // Animate cursor along curved path
    useEffect(() => {
        if (cursorRef.current) {
            const tl = gsap.timeline({ delay: 3 });

            gsap.set(cursorRef.current, { opacity: 0 });

            // Fade in
            tl.to(cursorRef.current, {
                opacity: 1,
                duration: 0.9,
            });

            // Move along curve
            tl.to(
                cursorRef.current,
                {
                    motionPath: {
                        path: "M200,150 C250,150 50,-50 25,8",
                        autoRotate: false,
                    },
                    duration: 1.4,
                    ease: "power2.out",
                },
                "<"
            );

            // Click - scale down
            tl.to(cursorRef.current, {
                scale: 0.9,
                duration: 0.1,
                ease: "power2.in",
            });

            // Click - scale back up
            tl.to(cursorRef.current, {
                scale: 1,
                duration: 0.1,
                ease: "power2.out",
            });

            // Fade out
            tl.to(cursorRef.current, {
                opacity: 0,
                duration: 0.5,
                delay: 0.3,
            });
        }
    }, []);

    // Enable scrolling for landing page (global styles have overflow: hidden)
    useEffect(() => {
        document.body.style.overflow = "auto";
        document.body.style.overflowX = "hidden";
        return () => {
            document.body.style.overflow = "hidden";
        };
    }, []);

    // Track page view
    useEffect(() => {
        trackPageView("/", document.referrer || "");
    }, []);

    // Track scroll events
    useEffect(() => {
        // Small delay to ensure DOM is fully rendered
        const timeoutId = setTimeout(() => {
            const sections = document.querySelectorAll("[data-scroll]");
            if (sections.length === 0) return;

            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            const name =
                                entry.target.getAttribute("data-scroll");
                            trackEvent(`scroll:${name}`);
                        }
                    });
                },
                { threshold: 0.1 }
            );

            sections.forEach((section) => observer.observe(section));
            window._scrollObserver = observer;
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            if (window._scrollObserver) {
                window._scrollObserver.disconnect();
                window._scrollObserver = null;
            }
        };
    }, []);

    const handleCTAClick = () => {
        trackEvent("click:google_signin");
        onGoogleSignIn();
    };

    return (
        <Page>
            <Logo
                href="https://heybjorn.com"
                target="_blank"
                rel="noopener noreferrer"
            />

            {/* Fixed CTA - appears when scrolled past hero */}
            <AnimatePresence>
                {!isHeroVisible && (
                    <FixedCTA
                        onClick={handleCTAClick}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 25,
                        }}
                        whileTap={{ scale: 0.995, y: 2 }}
                    >
                        <FixedCTAInner>Start free</FixedCTAInner>
                    </FixedCTA>
                )}
            </AnimatePresence>

            {/* Hero Section */}
            <Hero ref={heroRef} data-scroll="hero">
                <HeroInner>
                    <HeroContent>
                        <motion.div
                            initial="hidden"
                            animate={isHeroInView ? "visible" : "hidden"}
                            variants={stagger}
                        >
                            <Eyebrow variants={fadeUp}>
                                Try now for free
                            </Eyebrow>
                            <HeroTitle variants={fadeUp}>
                                Translate any TV subtitle.{" "}
                                <span className="highlight">Understand</span>{" "}
                                every word.
                            </HeroTitle>
                            <HeroSubtitle variants={fadeUp}>
                                Point your phone at any subtitle and get instant
                                translations. Highlight words or phrases to
                                build vocabulary and learn grammar. <br />
                                <br />A powerful learning partner for your
                                Spanish, German, or French journey – while you
                                watch Netflix.
                            </HeroSubtitle>
                            <motion.div variants={fadeUp}>
                                <CTAButton
                                    onClick={handleCTAClick}
                                    whileHover={{ scale: 1.005 }}
                                    whileTap={{ scale: 0.995 }}
                                >
                                    <CTAButtonInner>
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                        >
                                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                            <path
                                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                                fill="#34A853"
                                            />
                                            <path
                                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                                fill="#FBBC05"
                                            />
                                            <path
                                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                                fill="#EA4335"
                                            />
                                        </svg>
                                        Start free
                                    </CTAButtonInner>
                                </CTAButton>
                            </motion.div>
                        </motion.div>
                    </HeroContent>
                    <HeroVisual>
                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            style={{ position: "relative" }}
                        >
                            <BackgroundTV>
                                <video
                                    src={getVideoUrl("baking.mp4")}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                />
                            </BackgroundTV>
                            <PhoneMockup
                                style={{
                                    fontSize: "2em",
                                    marginTop: "1em",
                                }}
                            >
                                <PhoneScreen>
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={
                                            isHeroInView
                                                ? { opacity: 1, y: 0 }
                                                : {}
                                        }
                                        transition={{
                                            duration: 0.5,
                                            delay: 0.8,
                                        }}
                                    >
                                        <CaptionBubble>
                                            "Ich verstehe nicht, was du meinst"
                                        </CaptionBubble>
                                    </motion.div>
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={
                                            isHeroInView
                                                ? { opacity: 1, y: 0 }
                                                : {}
                                        }
                                        transition={{
                                            duration: 0.5,
                                            delay: 1.2,
                                        }}
                                    >
                                        <CaptionBubble className="translated">
                                            "C'est la vie, mon ami"
                                        </CaptionBubble>
                                    </motion.div>
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={
                                            isHeroInView
                                                ? { opacity: 1, y: 0 }
                                                : {}
                                        }
                                        transition={{
                                            duration: 0.5,
                                            delay: 1.8,
                                        }}
                                        style={{ position: "relative" }}
                                    >
                                        <CaptionBubble>
                                            "¿Dónde está la{" "}
                                            <HighlightWord
                                                initial={{
                                                    background: "transparent",
                                                }}
                                                animate={
                                                    isHeroInView
                                                        ? {
                                                              background:
                                                                  "rgba(255, 230, 181, 1)",
                                                          }
                                                        : {}
                                                }
                                                transition={{
                                                    duration: 0.3,
                                                    delay: 4.4,
                                                }}
                                            >
                                                biblioteca
                                                <Cursor ref={cursorRef}>
                                                    <img
                                                        src={getImageUrl(
                                                            "cursor.svg"
                                                        )}
                                                        alt=""
                                                    />
                                                </Cursor>
                                                <WordTooltip
                                                    initial={{
                                                        opacity: 0,
                                                        y: "20%",
                                                        x: "-50%",
                                                    }}
                                                    animate={
                                                        isHeroInView
                                                            ? {
                                                                  opacity: 1,
                                                                  y: "0%",
                                                                  x: "-50%",
                                                              }
                                                            : false
                                                    }
                                                    transition={{
                                                        y: {
                                                            type: "spring",
                                                            bounce: 0.15,
                                                            stiffness: 400,
                                                            damping: 22,
                                                            duration: 0.35,
                                                            delay: 4.4,
                                                        },
                                                        opacity: {
                                                            duration: 0.25,
                                                            ease: "easeOut",
                                                            delay: 4.4,
                                                        },
                                                    }}
                                                    onClick={() =>
                                                        setShowGlossary(true)
                                                    }
                                                    style={{
                                                        cursor: "pointer",
                                                    }}
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                >
                                                    <div className="tooltip-inner">
                                                        library
                                                        <div className="tooltip-btn">
                                                            <img
                                                                src={getImageUrl(
                                                                    "icon-search.svg"
                                                                )}
                                                                alt="search"
                                                            />
                                                        </div>
                                                    </div>
                                                </WordTooltip>
                                            </HighlightWord>
                                            ?"
                                        </CaptionBubble>
                                    </motion.div>
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={
                                            isHeroInView
                                                ? { opacity: 1, y: 0 }
                                                : {}
                                        }
                                        transition={{
                                            duration: 0.5,
                                            delay: 2.2,
                                        }}
                                    >
                                        <CaptionBubble className="translated">
                                            "Buongiorno, come stai?"
                                        </CaptionBubble>
                                    </motion.div>
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={
                                            isHeroInView ? { opacity: 1 } : {}
                                        }
                                        transition={{
                                            duration: 0.5,
                                            delay: 0.5,
                                        }}
                                        style={{ marginTop: "auto" }}
                                    >
                                        <VideoPlayer>
                                            <video
                                                src={getVideoUrl("baking.mp4")}
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                            />
                                        </VideoPlayer>
                                    </motion.div>
                                </PhoneScreen>
                            </PhoneMockup>
                        </motion.div>
                    </HeroVisual>
                </HeroInner>
            </Hero>

            {/* How It Works */}
            <HowItWorks ref={howItWorksRef} data-scroll="how-it-works">
                <SectionHeader>
                    <motion.div
                        initial="hidden"
                        animate={isHowItWorksInView ? "visible" : "hidden"}
                        variants={stagger}
                    >
                        <SectionTitle variants={fadeUp}>
                            How it works
                        </SectionTitle>
                        <SectionSubtitle variants={fadeUp}>
                            Start learning in seconds. No complicated setup.
                        </SectionSubtitle>
                    </motion.div>
                </SectionHeader>

                <StepsContainer>
                    {[
                        {
                            num: "1",
                            title: "Point at subtitles",
                            desc: "Aim your phone or second device at any foreign TV subtitles or captions.",
                        },
                        {
                            num: "2",
                            title: "Get instant translations",
                            desc: "See translations appear in real-time as you watch your favorite shows.",
                        },
                        {
                            num: "3",
                            title: "Build your vocabulary",
                            desc: "Tap any word for definitions, grammar, and example sentences.",
                        },
                    ].map((step, i) => (
                        <Step
                            key={step.num}
                            initial={{ opacity: 0, y: 30 }}
                            animate={
                                isHowItWorksInView ? { opacity: 1, y: 0 } : {}
                            }
                            transition={{
                                delay: 0.2 + i * 0.15,
                                duration: 0.6,
                            }}
                        >
                            <StepNumber>{step.num}</StepNumber>
                            <StepTitle>{step.title}</StepTitle>
                            <StepDesc>{step.desc}</StepDesc>
                        </Step>
                    ))}
                </StepsContainer>
            </HowItWorks>

            {/* Features */}
            <Features ref={featuresRef} data-scroll="features">
                <FeatureGrid>
                    {/* Feature 1: Real-time Translation */}
                    <FeatureRow
                        data-scroll="feature-translation"
                        initial={{ opacity: 0, x: -50 }}
                        animate={isFeaturesInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.8 }}
                    >
                        <FeatureContent>
                            <FeatureLabel>Works everywhere</FeatureLabel>
                            <FeatureTitle>
                                Live translations from any screen
                            </FeatureTitle>
                            <FeatureDesc>
                                TV Translate reads and translates subtitles
                                from TV, Netflix, YouTube, any streaming service
                                or a live broadcast.
                            </FeatureDesc>
                        </FeatureContent>
                        <FeatureVisual>
                            <BackgroundTV>
                                <video
                                    src={getVideoUrl("baking.mp4")}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                />
                            </BackgroundTV>
                            <PhoneMockup>
                                <PhoneScreen>
                                    <CaptionBubble>
                                        "Ich verstehe nicht, was du meinst"
                                    </CaptionBubble>
                                    <CaptionBubble className="translated">
                                        "I don't understand what you mean"
                                    </CaptionBubble>
                                    <VideoPlayer>
                                        <video
                                            src={getVideoUrl("baking.mp4")}
                                            autoPlay
                                            loop
                                            muted
                                            playsInline
                                        />
                                    </VideoPlayer>
                                </PhoneScreen>
                            </PhoneMockup>
                        </FeatureVisual>
                    </FeatureRow>

                    {/* Feature 2: Word Glossary */}
                    <FeatureRow
                        data-scroll="feature-vocabulary"
                        initial={{ opacity: 0, x: 50 }}
                        animate={isFeaturesInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <FeatureContent>
                            <FeatureLabel>Connected to Björn</FeatureLabel>
                            <FeatureTitle>
                                Build vocabulary while you watch
                            </FeatureTitle>
                            <FeatureDesc>
                                Highlight a word or phrase for its meaning,
                                conjugations and examples, then add it to your
                                Björn vocabulary
                                decks. Practise your saved words later at{" "}
                                <ProductLink
                                    href="https://heybjorn.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    heybjorn.com
                                </ProductLink>
                                .
                            </FeatureDesc>
                        </FeatureContent>
                        <FeatureVisual>
                            <PhoneMockup>
                                <PhoneScreen>
                                    <CaptionBubble>
                                        "Das ist eine{" "}
                                        <HighlightWord
                                            style={{
                                                background:
                                                    "rgba(255, 230, 181, 1)",
                                            }}
                                        >
                                            wunderbare
                                            <Cursor
                                                style={{
                                                    opacity: 1,
                                                    top: "0.5em",
                                                    left: "2em",
                                                }}
                                            >
                                                <img
                                                    src={getImageUrl(
                                                        "cursor.svg"
                                                    )}
                                                    alt=""
                                                />
                                            </Cursor>
                                        </HighlightWord>{" "}
                                        Idee!"
                                    </CaptionBubble>
                                </PhoneScreen>
                            </PhoneMockup>
                            <GlossaryPreview>
                                <div className="word">wunderbar</div>
                                <div className="definition">
                                    adj. wonderful, marvelous
                                    <br />
                                    <br />
                                    • wunderbarer (masc.)
                                    <br />
                                    • wunderbare (fem.)
                                    <br />• wunderbares (neut.)
                                </div>
                            </GlossaryPreview>
                        </FeatureVisual>
                    </FeatureRow>

                    {/* Feature 3: Two-Device Mode */}
                    <FeatureRow
                        data-scroll="feature-two-device"
                        initial={{ opacity: 0, x: -50 }}
                        animate={isFeaturesInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.8, delay: 0.4 }}
                    >
                        <FeatureContent>
                            <FeatureLabel>Two-device viewing</FeatureLabel>
                            <FeatureTitle>
                                Hand-off from your second device
                            </FeatureTitle>
                            <FeatureDesc>
                                Point your laptop or second device at the
                                TV and follow the translations comfortably on
                                your phone. Watch a whole programme without
                                holding anything up.
                            </FeatureDesc>
                        </FeatureContent>
                        <FeatureVisual>
                            <BackgroundTV>
                                <video
                                    src={getVideoUrl("baking.mp4")}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                />
                            </BackgroundTV>
                            <PhoneMockup>
                                <PhoneScreen>
                                    <div
                                        style={{
                                            fontSize: "0.5em",
                                            opacity: 0.6,
                                            textAlign: "center",
                                            marginBottom: "0.5em",
                                        }}
                                    >
                                        Capturing...
                                    </div>
                                    <VideoPlayer>
                                        <video
                                            src={getVideoUrl("baking.mp4")}
                                            autoPlay
                                            loop
                                            muted
                                            playsInline
                                        />
                                    </VideoPlayer>
                                </PhoneScreen>
                            </PhoneMockup>
                            <PhoneMockup className="secondary">
                                <PhoneScreen>
                                    <CaptionBubble>
                                        "Ich verstehe nicht, was du meinst"
                                    </CaptionBubble>
                                    <CaptionBubble className="translated">
                                        "I don't understand what you mean"
                                    </CaptionBubble>
                                </PhoneScreen>
                            </PhoneMockup>
                        </FeatureVisual>
                    </FeatureRow>
                </FeatureGrid>
            </Features>

            {/* Final CTA */}
            <FinalCTA ref={ctaRef} data-scroll="cta">
                <motion.div
                    initial="hidden"
                    animate={isCtaInView ? "visible" : "hidden"}
                    variants={stagger}
                >
                    <FinalTitle variants={fadeUp}>
                        Settle in and{" "}
                        <span className="highlight">understand</span> more
                    </FinalTitle>
                    <motion.div variants={fadeUp}>
                        <CTAButton
                            onClick={handleCTAClick}
                            whileTap={{ scale: 0.995 }}
                        >
                            <CTAButtonInner>
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path
                                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                        fill="#34A853"
                                    />
                                    <path
                                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                        fill="#FBBC05"
                                    />
                                    <path
                                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                        fill="#EA4335"
                                    />
                                </svg>
                                Continue with Google
                            </CTAButtonInner>
                        </CTAButton>
                    </motion.div>
                    <TrustBadges variants={fadeUp}>
                        <Badge>
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            </svg>
                            Secure & Private
                        </Badge>
                        <Badge>
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                            </svg>
                            Setup in seconds
                        </Badge>
                        <Badge>
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                            </svg>
                            Made for learners
                        </Badge>
                    </TrustBadges>
                </motion.div>
            </FinalCTA>

            <Footer>
                <p>
                    © {new Date().getFullYear()}{" "}
                    <a
                        href="https://heybjorn.com"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Björn
                    </a>
                    . Learn languages while you watch.
                </p>
            </Footer>

            {/* Glossary Modal */}
            <AnimatePresence>
                {showGlossary && (
                    <GlossaryBackdrop
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={() => setShowGlossary(false)}
                    >
                        <GlossaryModal
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{
                                opacity: 0,
                                y: 20,
                                transition: { duration: 0.2 },
                            }}
                            transition={{
                                y: {
                                    type: "spring",
                                    bounce: 1,
                                    stiffness: 300,
                                    damping: 16,
                                },
                                opacity: { duration: 0.2 },
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <GlossaryCloseButton
                                onClick={() => setShowGlossary(false)}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.2 }}
                                whileTap={{ scale: 0.95 }}
                            />
                            <GlossaryHeader>
                                <GlossaryWordTitle>
                                    biblioteca
                                </GlossaryWordTitle>
                                <GlossaryTranslation>
                                    library
                                </GlossaryTranslation>
                                <GlossaryWordType>
                                    noun, feminine
                                </GlossaryWordType>
                            </GlossaryHeader>

                            <GlossarySection>
                                <GlossarySectionTitle>
                                    Variations
                                </GlossarySectionTitle>
                                <GlossaryVariationsGrid>
                                    <GlossaryVariationItem>
                                        <GlossaryVariationLabel>
                                            Singular
                                        </GlossaryVariationLabel>
                                        <GlossaryVariationValue>
                                            la biblioteca
                                        </GlossaryVariationValue>
                                        <GlossaryVariationTranslation>
                                            the library
                                        </GlossaryVariationTranslation>
                                    </GlossaryVariationItem>
                                    <GlossaryVariationItem>
                                        <GlossaryVariationLabel>
                                            Plural
                                        </GlossaryVariationLabel>
                                        <GlossaryVariationValue>
                                            las bibliotecas
                                        </GlossaryVariationValue>
                                        <GlossaryVariationTranslation>
                                            the libraries
                                        </GlossaryVariationTranslation>
                                    </GlossaryVariationItem>
                                    <GlossaryVariationItem>
                                        <GlossaryVariationLabel>
                                            Diminutive
                                        </GlossaryVariationLabel>
                                        <GlossaryVariationValue>
                                            la bibliotequita
                                        </GlossaryVariationValue>
                                        <GlossaryVariationTranslation>
                                            the little library
                                        </GlossaryVariationTranslation>
                                    </GlossaryVariationItem>
                                </GlossaryVariationsGrid>
                            </GlossarySection>

                            <GlossarySection>
                                <GlossarySectionTitle>
                                    Similar Phrases
                                </GlossarySectionTitle>
                                <GlossaryPhraseItem>
                                    <GlossaryPhrase>
                                        ir a la biblioteca
                                    </GlossaryPhrase>
                                    <GlossaryPhraseTranslation>
                                        to go to the library
                                    </GlossaryPhraseTranslation>
                                </GlossaryPhraseItem>
                                <GlossaryPhraseItem>
                                    <GlossaryPhrase>
                                        biblioteca pública
                                    </GlossaryPhrase>
                                    <GlossaryPhraseTranslation>
                                        public library
                                    </GlossaryPhraseTranslation>
                                </GlossaryPhraseItem>
                                <GlossaryPhraseItem>
                                    <GlossaryPhrase>
                                        tarjeta de biblioteca
                                    </GlossaryPhrase>
                                    <GlossaryPhraseTranslation>
                                        library card
                                    </GlossaryPhraseTranslation>
                                </GlossaryPhraseItem>
                            </GlossarySection>

                            <GlossarySection>
                                <GlossarySectionTitle>
                                    Example Sentences
                                </GlossarySectionTitle>
                                <GlossaryPhraseItem>
                                    <GlossaryPhrase>
                                        Voy a estudiar en la biblioteca.
                                    </GlossaryPhrase>
                                    <GlossaryPhraseTranslation>
                                        I'm going to study at the library.
                                    </GlossaryPhraseTranslation>
                                </GlossaryPhraseItem>
                                <GlossaryPhraseItem>
                                    <GlossaryPhrase>
                                        La biblioteca cierra a las ocho.
                                    </GlossaryPhrase>
                                    <GlossaryPhraseTranslation>
                                        The library closes at eight.
                                    </GlossaryPhraseTranslation>
                                </GlossaryPhraseItem>
                            </GlossarySection>
                        </GlossaryModal>
                    </GlossaryBackdrop>
                )}
            </AnimatePresence>
        </Page>
    );
};

export default LandingPage;
